import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import { Loader2, Plus, Trash2, Package, Minus, Briefcase } from 'lucide-react';
import { useCreateInvoice, useUpdateInvoice, useNextInvoiceNumber } from '@/hooks/queries/useInvoices';
import { useClients } from '@/hooks/queries/useClients';
import { useProducts } from '@/hooks/queries/useProducts';
import { useServices } from '@/hooks/queries/useServices';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/components/ui/toast/useToast';
import type { Invoice, Product, Service } from '@lebusiness/types';

const invoiceSchema = z.object({
  type: z.enum(['quote', 'proforma', 'invoice', 'credit_note', 'delivery_note']),
  invoiceNumber: z.string().min(1, 'رقم الفاتورة مطلوب / Numéro requis'),
  clientId: z.string().min(1, 'العميل مطلوب / Client requis'),
  date: z.date(),
  dueDate: z.date().optional().nullable(),
  status: z.enum(['draft', 'sent', 'paid', 'partial', 'overdue', 'cancelled']),
  notes: z.string().optional(),
  termsAndConditions: z.string().optional(),
});

type InvoiceFormData = z.infer<typeof invoiceSchema>;

interface InvoiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice?: Invoice | null;
  onSuccess?: () => void;
}

interface LineItem {
  productId?: string | null;
  product?: Product;
  serviceId?: string | null;
  service?: Service;
  itemType: 'product' | 'service';
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discount: number;
  discountType: 'percentage' | 'amount';
  vat: number;
}

export function InvoiceForm({ open, onOpenChange, invoice, onSuccess }: InvoiceFormProps) {
  const { company } = useAuthStore();
  const { data: clients = [] } = useClients(company?.id || '');
  const { data: products = [] } = useProducts(company?.id || '');
  const { data: services = [] } = useServices(company?.id || '');
  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();
  const { data: nextNumber, refetch: refetchNumber } = useNextInvoiceNumber(
    company?.id || '',
    'invoice'
  );

  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [selectedType, setSelectedType] = useState<string>('invoice');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: invoice
      ? {
          type: invoice.type,
          invoiceNumber: invoice.invoiceNumber,
          clientId: invoice.clientId,
          date: new Date(invoice.date),
          dueDate: invoice.dueDate ? new Date(invoice.dueDate) : null,
          status: invoice.status,
          notes: invoice.notes || '',
          termsAndConditions: invoice.termsAndConditions || '',
        }
      : {
          type: 'invoice',
          invoiceNumber: nextNumber || 'INV-2025-00001',
          date: new Date(),
          dueDate: null,
          status: 'draft',
        },
  });

  // Reset form when dialog opens/closes or invoice changes
  useEffect(() => {
    if (open) {
      if (invoice) {
        // Editing existing invoice
        setSelectedType(invoice.type);
        reset({
          type: invoice.type,
          invoiceNumber: invoice.invoice_number,
          clientId: invoice.client_id,
          date: new Date(invoice.date),
          dueDate: invoice.due_date ? new Date(invoice.due_date) : null,
          status: invoice.status,
          notes: invoice.notes || '',
          termsAndConditions: invoice.terms_and_conditions || '',
        });
        
        // Load invoice items with proper field names
        if (invoice.invoice_items && invoice.invoice_items.length > 0) {
          const items: LineItem[] = invoice.invoice_items.map((item: any) => ({
            productId: item.product_id || null,
            product: item.products || undefined,
            serviceId: item.service_id || null,
            service: item.services || undefined,
            itemType: item.service_id ? 'service' : 'product',
            description: item.description,
            quantity: item.quantity,
            unit: item.unit || 'pcs',
            unitPrice: item.unit_price,
            discount: item.discount || 0,
            discountType: item.discount_type || 'percentage',
            vat: item.vat,
          }));
          setLineItems(items);
        }
      } else {
        // Creating new invoice
        setSelectedType('invoice');
        refetchNumber();
        reset({
          type: 'invoice',
          invoiceNumber: nextNumber || 'INV-2025-00001',
          date: new Date(),
          dueDate: null,
          status: 'draft',
          notes: '',
          termsAndConditions: '',
        });
        setLineItems([]);
      }
    }
  }, [open, invoice, reset, nextNumber, refetchNumber]);

  // Auto-update invoice number when type changes (new invoices only)
  useEffect(() => {
    if (open && !invoice && nextNumber) {
      setValue('invoiceNumber', nextNumber);
    }
  }, [nextNumber, invoice, setValue, open, selectedType]);

  // Calculate totals
  const calculations = calculateInvoiceTotals(lineItems);

  const addProductLineItem = (productId: string) => {
    const product = products.find((p: any) => p.id === productId);
    if (!product) return;

    // Check if product has sufficient stock
    if ((product as any).stock_quantity < 1) {
      toast.error(
        'مخزون غير كافٍ / Stock épuisé',
        `${product.name}: Stock épuisé (0 disponible)`
      );
      return;
    }

    const newItem: LineItem = {
      productId: product.id,
      product,
      serviceId: null,
      itemType: 'product',
      description: product.name,
      quantity: 1,
      unit: 'pcs',
      unitPrice: (product as any).sale_price,
      discount: 0,
      discountType: 'percentage',
      vat: product.vat,
    };

    setLineItems([...lineItems, newItem]);
  };

  const addServiceLineItem = (serviceId: string) => {
    const service = services.find((s: any) => s.id === serviceId);
    if (!service) return;

    const newItem: LineItem = {
      productId: null,
      serviceId: service.id,
      service,
      itemType: 'service',
      description: service.name,
      quantity: 1,
      unit: (service as any).unit || 'fixed',
      unitPrice: (service as any).sale_price || service.salePrice || 0,
      discount: 0,
      discountType: 'percentage',
      vat: service.vat,
    };

    setLineItems([...lineItems, newItem]);
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const validateStock = (): string[] => {
    const errors: string[] = [];
    lineItems.forEach((item) => {
      // Only validate stock for product items (services have no stock)
      if (item.itemType === 'product' && item.product && item.quantity > (item.product as any).stock_quantity) {
        errors.push(
          `${item.product.name}: Stock insuffisant (${(item.product as any).stock_quantity} disponible)`
        );
      }
    });
    return errors;
  };

  const onSubmit = async (data: InvoiceFormData) => {
    if (!company?.id) return;

    // Validate we have items
    if (lineItems.length === 0) {
      toast.error('خطأ / Erreur', 'يجب إضافة عنصر واحد على الأقل / Ajoutez au moins un article');
      return;
    }

    // Validate stock
    const stockErrors = validateStock();
    if (stockErrors.length > 0 && data.type === 'invoice') {
      toast.error('مخزون غير كافٍ / Stock insuffisant', stockErrors.join('\n'));
      return;
    }

    try {
      const invoiceItems = lineItems.map((item) => ({
        product_id: item.productId || null,
        service_id: item.serviceId || null,
        description: item.description,
        quantity: item.quantity,
        unit: item.itemType === 'product' ? (item.product?.unit || 'pcs') : item.unit,
        unit_price: item.unitPrice,
        discount: item.discount || 0,
        discount_type: item.discountType,
        vat: item.vat,
        total: calculateLineTotal(item),
      }));

      const invoiceData = {
        type: data.type,
        invoice_number: data.invoiceNumber,
        client_id: data.clientId,
        company_id: company.id,
        date: data.date.toISOString(),
        due_date: data.dueDate?.toISOString() || null,
        status: data.status,
        subtotal: calculations.subtotal,
        vat_amount: calculations.vatAmount,
        fiscal_stamp: calculations.fiscalStamp,
        discount: 0,
        total: calculations.total,
        paid: 0,
        balance: calculations.total,
        notes: data.notes || null,
        terms_and_conditions: data.termsAndConditions || null,
        language: 'fr' as const,
        sync_status: 'synced' as const,
      };

      if (invoice) {
        await updateInvoice.mutateAsync({
          id: invoice.id,
          ...invoiceData,
          items: invoiceItems,
        });
        toast.success(
          'تم التحديث بنجاح / Modifiée avec succès',
          'تم تحديث الفاتورة / La facture a été modifiée'
        );
      } else {
        await createInvoice.mutateAsync({
          ...invoiceData,
          items: invoiceItems,
        });
        toast.success(
          'تم الإنشاء بنجاح / Créée avec succès',
          'تم إنشاء الفاتورة / La facture a été créée'
        );
      }

      onSuccess?.();
      reset();
      setLineItems([]);
      setSelectedType('invoice');
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error(
        'خطأ / Erreur',
        error instanceof Error ? error.message : 'فشل حفظ الفاتورة / Échec de l\'enregistrement'
      );
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {invoice ? 'تعديل الفاتورة / Modifier la facture' : 'فاتورة جديدة / Nouvelle facture'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Header Section */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>النوع / Type *</Label>
              <Select
                value={selectedType}
                onValueChange={(value) => {
                  setSelectedType(value);
                  setValue('type', value as any);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quote">عرض سعر / Devis</SelectItem>
                  <SelectItem value="proforma">فاتورة أولية / Proforma</SelectItem>
                  <SelectItem value="invoice">فاتورة / Facture</SelectItem>
                  <SelectItem value="credit_note">إشعار دائن / Avoir</SelectItem>
                  <SelectItem value="delivery_note">وصل تسليم / BL</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoiceNumber">الرقم / Numéro *</Label>
              <Input id="invoiceNumber" {...register('invoiceNumber')} />
              {errors.invoiceNumber && (
                <p className="text-sm text-red-600">{errors.invoiceNumber.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>التاريخ / Date *</Label>
              <DatePicker
                value={watch('date')}
                onChange={(date) => setValue('date', date || new Date())}
              />
            </div>

            <div className="space-y-2">
              <Label>تاريخ الاستحقاق / Échéance</Label>
              <DatePicker
                value={watch('dueDate')}
                onChange={(date) => setValue('dueDate', date)}
                placeholder="اختياري / Optionnel"
              />
            </div>
          </div>

          {/* Client Selection */}
          <div className="space-y-2">
            <Label>العميل / Client *</Label>
            <Combobox
              items={clients}
              value={watch('clientId')}
              onValueChange={(value) => setValue('clientId', value)}
              getLabel={(client) => client.name}
              getValue={(client) => client.id}
              getDescription={(client) => `${client.phone} - ${client.wilaya}`}
              placeholder="اختر العميل / Sélectionner un client"
              searchPlaceholder="بحث... / Rechercher..."
              emptyText="لا يوجد عملاء / Aucun client"
            />
            {errors.clientId && (
              <p className="text-sm text-red-600">{errors.clientId.message}</p>
            )}
          </div>

          {/* Line Items Section */}
          <div className="space-y-4">
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">العناصر / Articles</h3>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Combobox
                    items={products.filter((p: any) => p.is_active)}
                    value=""
                    onValueChange={addProductLineItem}
                    getLabel={(product: any) => `${product.code} - ${product.name}`}
                    getValue={(product: any) => product.id}
                    getDescription={(product: any) =>
                      `Stock: ${product.stock_quantity} ${product.unit} | ${formatCurrency(product.sale_price)}`
                    }
                    placeholder="📦 إضافة منتج / Ajouter un produit"
                    searchPlaceholder="بحث... / Rechercher..."
                    emptyText="لا توجد منتجات / Aucun produit"
                  />
                </div>
                <div className="flex-1">
                  <Combobox
                    items={services.filter((s: any) => s.is_active !== false)}
                    value=""
                    onValueChange={addServiceLineItem}
                    getLabel={(service: any) => `${service.code} - ${service.name}`}
                    getValue={(service: any) => service.id}
                    getDescription={(service: any) =>
                      `${service.unit} | ${formatCurrency(service.sale_price || 0)}`
                    }
                    placeholder="🔧 إضافة خدمة / Ajouter un service"
                    searchPlaceholder="بحث... / Rechercher..."
                    emptyText="لا توجد خدمات / Aucun service"
                  />
                </div>
              </div>
            </div>

            {lineItems.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <div className="flex justify-center gap-3 mb-2">
                  <Package className="w-10 h-10 text-gray-400" />
                  <Briefcase className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-600">
                  لم تتم إضافة أي عناصر / Aucun article ajouté
                </p>
                <p className="text-sm text-gray-500">
                  أضف منتجات أو خدمات / Ajoutez des produits ou services
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                        العنصر / Article
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                        الكمية / Qté
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                        الوحدة / Unité
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                        السعر / Prix
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                        الخصم / Remise
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                        TVA
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                        المجموع / Total
                      </th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {lineItems.map((item, index) => (
                      <LineItemRow
                        key={index}
                        item={item}
                        index={index}
                        onUpdate={updateLineItem}
                        onRemove={removeLineItem}
                        formatCurrency={formatCurrency}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Totals Panel */}
          {lineItems.length > 0 && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="space-y-2 max-w-md ml-auto">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">الإجمالي الفرعي / Sous-total:</span>
                  <span className="font-medium">{formatCurrency(calculations.subtotal)}</span>
                </div>
                {calculations.vat9 > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">TVA 9%:</span>
                    <span className="font-medium">{formatCurrency(calculations.vat9)}</span>
                  </div>
                )}
                {calculations.vat19 > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">TVA 19%:</span>
                    <span className="font-medium">{formatCurrency(calculations.vat19)}</span>
                  </div>
                )}
                {calculations.fiscalStamp > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">الطابع الجبائي / Timbre fiscal:</span>
                    <span className="font-medium">{formatCurrency(calculations.fiscalStamp)}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-bold text-lg">المجموع / TOTAL:</span>
                  <span className="font-bold text-lg text-emerald-600">
                    {formatCurrency(calculations.total)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Footer Section */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="notes">ملاحظات / Notes</Label>
              <Textarea id="notes" {...register('notes')} rows={3} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="termsAndConditions">الشروط / Conditions</Label>
              <Textarea id="termsAndConditions" {...register('termsAndConditions')} rows={3} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>الحالة / Statut</Label>
              <Select
                value={watch('status')}
                onValueChange={(value) => setValue('status', value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">مسودة / Brouillon</SelectItem>
                  <SelectItem value="sent">مرسلة / Envoyée</SelectItem>
                  <SelectItem value="paid">مدفوعة / Payée</SelectItem>
                  <SelectItem value="partial">جزئي / Partiel</SelectItem>
                  <SelectItem value="overdue">متأخرة / En retard</SelectItem>
                  <SelectItem value="cancelled">ملغاة / Annulée</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء / Annuler
            </Button>
            <Button
              type="submit"
              disabled={createInvoice.isPending || updateInvoice.isPending}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              {(createInvoice.isPending || updateInvoice.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {invoice ? 'حفظ / Enregistrer' : 'إنشاء / Créer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Line Item Row Component
interface LineItemRowProps {
  item: LineItem;
  index: number;
  onUpdate: (index: number, field: keyof LineItem, value: any) => void;
  onRemove: (index: number) => void;
  formatCurrency: (amount: number) => string;
}

function LineItemRow({ item, index, onUpdate, onRemove, formatCurrency }: LineItemRowProps) {
  const lineTotal = calculateLineTotal(item);
  const isService = item.itemType === 'service';
  const hasInsufficientStock = !isService && item.product && item.quantity > (item.product as any).stock_quantity;

  return (
    <tr className={hasInsufficientStock ? 'bg-red-50' : ''}>
      <td className="px-3 py-2">
        <div>
          <div className="flex items-center gap-1.5">
            {isService ? (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">SRV</span>
            ) : (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">PRD</span>
            )}
            <p className="text-sm font-medium">
              {isService ? (item.service?.name || item.description) : (item.product?.name || item.description)}
            </p>
          </div>
          <p className="text-xs text-gray-500">
            {isService ? ((item.service as any)?.code || '') : ((item.product as any)?.code || '')}
          </p>
          {hasInsufficientStock && (
            <p className="text-xs text-red-600 font-semibold">
              Stock insuffisant! Disponible: {(item.product as any)?.stock_quantity}
            </p>
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onUpdate(index, 'quantity', Math.max(0, item.quantity - 1))}
            className="h-7 w-7 p-0"
          >
            <Minus className="h-3 w-3" />
          </Button>
          <Input
            type="number"
            step="0.01"
            value={item.quantity.toFixed(2)}
            onChange={(e) => onUpdate(index, 'quantity', parseFloat(e.target.value) || 0)}
            className="w-20 h-7 text-sm text-center"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onUpdate(index, 'quantity', item.quantity + 1)}
            className="h-7 w-7 p-0"
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </td>
      <td className="px-3 py-2">
        <Select
          value={item.unit || 'pcs'}
          onValueChange={(value) => onUpdate(index, 'unit', value)}
        >
          <SelectTrigger className="w-24 h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pcs">Unité</SelectItem>
            <SelectItem value="kg">Kg</SelectItem>
            <SelectItem value="m">Mètre</SelectItem>
            <SelectItem value="m2">M²</SelectItem>
            <SelectItem value="m3">M³</SelectItem>
            <SelectItem value="l">Litre</SelectItem>
            <SelectItem value="box">Boîte</SelectItem>
            <SelectItem value="pack">Pack</SelectItem>
            <SelectItem value="hour">Heure</SelectItem>
            <SelectItem value="day">Jour</SelectItem>
            <SelectItem value="session">Séance</SelectItem>
            <SelectItem value="project">Projet</SelectItem>
            <SelectItem value="fixed">Forfait</SelectItem>
            <SelectItem value="month">Mois</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          step="0.01"
          value={item.unitPrice}
          onChange={(e) => onUpdate(index, 'unitPrice', parseFloat(e.target.value) || 0)}
          className="w-24 h-8 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <Input
            type="number"
            step="0.01"
            value={item.discount}
            onChange={(e) => onUpdate(index, 'discount', parseFloat(e.target.value) || 0)}
            className="w-16 h-8 text-sm"
          />
          <Select
            value={item.discountType}
            onValueChange={(value) => onUpdate(index, 'discountType', value)}
          >
            <SelectTrigger className="w-12 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">%</SelectItem>
              <SelectItem value="amount">DA</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </td>
      <td className="px-3 py-2">
        <span className="text-sm">{item.vat}%</span>
      </td>
      <td className="px-3 py-2">
        <span className="text-sm font-medium">{formatCurrency(lineTotal)}</span>
      </td>
      <td className="px-3 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          className="h-8 w-8 p-0"
        >
          <Trash2 className="h-4 w-4 text-red-600" />
        </Button>
      </td>
    </tr>
  );
}

// Calculation helpers
function calculateLineTotal(item: LineItem): number {
  const subtotal = item.quantity * item.unitPrice;
  const discount =
    item.discountType === 'percentage' ? subtotal * (item.discount / 100) : item.discount;
  const afterDiscount = subtotal - discount;
  const vatAmount = afterDiscount * (item.vat / 100);
  return afterDiscount + vatAmount;
}

function calculateInvoiceTotals(items: LineItem[]) {
  let subtotal = 0;
  let vat9 = 0;
  let vat19 = 0;

  items.forEach((item) => {
    const lineSubtotal = item.quantity * item.unitPrice;
    const discount =
      item.discountType === 'percentage'
        ? lineSubtotal * (item.discount / 100)
        : item.discount;
    const afterDiscount = lineSubtotal - discount;

    subtotal += afterDiscount;

    if (item.vat === 9) {
      vat9 += afterDiscount * 0.09;
    } else if (item.vat === 19) {
      vat19 += afterDiscount * 0.19;
    }
  });

  const vatAmount = vat9 + vat19;
  const fiscalStamp = subtotal > 5000 ? 100 : 0;
  const total = subtotal + vatAmount + fiscalStamp;

  return { subtotal, vatAmount, vat9, vat19, fiscalStamp, total };
}
